import { isNativeNetworkAvailable, subscribeToNativeNetwork } from './nativeNetwork';
import {
  isBackendReachable,
  subscribeBackendReachability,
} from './backendReachability';

/**
 * Fuente ÚNICA de conectividad del subsistema offline.
 *
 * En el WebView de Android `navigator.onLine` llega tarde o no llega (ver
 * `nativeNetwork.ts`); el banner ya prefería el estado del sistema, pero el
 * link de la cola, el `RetryLink` y los sync managers seguían leyendo
 * `navigator.onLine`, así que podían decidir distinto sobre la misma tablet.
 * Aquí decide uno solo: en nativo, el último estado que dio Android (o
 * `navigator.onLine` mientras no haya llegado ninguno); en web,
 * `navigator.onLine` tal cual — que es lo que emulan las E2E.
 *
 * ⚠️ Esto dice si la app SE CREE online. Con una raya de cobertura dice `true`
 * aunque no pasen datos: la red degradada no la detecta ninguna bandera, solo
 * el tiempo (ver el techo de espera de `OfflineQueueLink`).
 */
type Listener = (online: boolean) => void;

let nativeConnected: boolean | null = null;
let listeners = new Set<Listener>();
let started = false;
let stopNative: (() => void) | null = null;

const notify = (): void => {
  const online = isEffectivelyOnline();
  listeners.forEach((listener) => listener(online));
};

const onWindowChange = (): void => notify();

const start = (): void => {
  if (started) return;
  started = true;
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onWindowChange);
    window.addEventListener('offline', onWindowChange);
  }
  // Un cambio de veredicto del sondeo es un cambio de conectividad EFECTIVA:
  // hay que avisar igual que de un `online`/`offline` del sistema.
  subscribeBackendReachability(notify);
  if (isNativeNetworkAvailable()) {
    stopNative = subscribeToNativeNetwork((connected) => {
      nativeConnected = connected;
      notify();
    });
  }
};

/** ¿La app se cree online ahora mismo? (nativo: Android; web: `navigator.onLine`) */
export function isOnlineNow(): boolean {
  start();
  if (isNativeNetworkAvailable() && nativeConnected !== null) return nativeConnected;
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

/**
 * ¿Se puede hablar con el backend ahora mismo?
 *
 * Es la pregunta que le importa a la app: manda el veredicto del sondeo cuando
 * lo hay, y si no, el estado de red del dispositivo. Un dispositivo enganchado
 * a una red que no llega a ninguna parte cuenta como SIN conexión.
 */
export function isEffectivelyOnline(): boolean {
  if (!isOnlineNow()) return false;
  return isBackendReachable() !== false;
}

/** Avisa de cada cambio de conectividad. Devuelve la función para dejar de escuchar. */
export function subscribeConnectivity(listener: Listener): () => void {
  start();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Solo para tests: vuelve al estado de módulo recién cargado. */
export function _resetConnectivityForTests(): void {
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', onWindowChange);
    window.removeEventListener('offline', onWindowChange);
  }
  stopNative?.();
  stopNative = null;
  nativeConnected = null;
  listeners = new Set();
  started = false;
}
